/**
 * Responsibility: Implement world sim behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState, WorldEvent } from "@anyquest/shared-core"
import { QUEST_VALID_TRANSITIONS } from "@anyquest/shared-core"
import { runWorldTick as aiWorldTick, runWorldEnrichment as aiWorldEnrich } from '../ai/logicForge'
import { dispatcher } from '../app-states'
import { TOPICS } from "@anyquest/shared-core"
import { formatDay, formatClock, totalMinFromClock } from './temporal'
import { applyProgressClockUpdates, formatProgressClock, locationKey, parseLocKey, generateNpcId, normalizeSex } from './state'
import { isMetaSystemNpcName } from './npc-guard'
import { canWorldTickSetQuestStatus } from './world-sim-quests'
// ============================================================
// WORLD-SIM.TS — Living World Tick Engine
// Runs AI world simulation tasks periodically to make the world
// feel alive — NPCs roam, quests progress, new locations emerge.
//
// Two triggers:
//   1. Real-time: setInterval every 90s (fires when not processing)
//   2. Action-based: checkAndTick() called by game-store after each action
//
// Circular dependency note: game-store imports world-sim AND world-sim
// must call game-store.save(). We resolve this by:
//   - game-store calls setWorldSimState() after loading/starting
//   - save() + isProcessing getter are injected via setWorldSimCallbacks()
// ============================================================

const TICK_INTERVAL_MS = 90_000;  // 90 seconds real-time
const GAME_MIN_PER_TICK = 60;     // 60 game-minutes between ticks
const MAX_WORLD_LOG = 30
let tickTimer: ReturnType<typeof setInterval> | null = null
let enrichmentTimer: ReturnType<typeof setTimeout> | null = null
let isSimulating = false
let simState: GameState | null = null
let saveCallback: (() => Promise<void>) | null = null
let isProcessingGetter: (() => boolean) = () => false
function normalizeActorName(name: string | undefined): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/^the\s+/, '')
}

function isPlayerEchoNpc(gs: GameState, npcName: string | undefined): boolean {
  const normalized = normalizeActorName(npcName)
  if (!normalized) return false
  if (isMetaSystemNpcName(npcName)) return true
  if (normalized === 'player' || normalized === 'you') return true
  const playerIdentity = normalizeActorName(gs.player.identity)
  const playerName = normalizeActorName(gs.player.name)
  return (!!playerIdentity && normalized === playerIdentity) || (!!playerName && normalized === playerName)
}

function sanitizePlayerEchoNpcs(gs: GameState): void {
  gs.world.npcs = (gs.world.npcs ?? []).filter(n => !isPlayerEchoNpc(gs, n.name))
  gs.currentRoom.npcs = (gs.currentRoom.npcs ?? []).filter(n => !isPlayerEchoNpc(gs, n.name))
}

// ---- Dependency injection (called by game-store) ----

/** Called by game-store after state is set (load or new game). Clears enrichment timer on null. */
export function setWorldSimState(gs: GameState | null): void {
  if (gs) sanitizePlayerEchoNpcs(gs)
  simState = gs
  if (!gs && enrichmentTimer !== null) {
    clearTimeout(enrichmentTimer)
    enrichmentTimer = null
  }
}

/** Inject the save function and processing getter from game-store (avoids circular import). */
export function setWorldSimCallbacks(
  saveFn: () => Promise<void>,
  processingGetter: () => boolean,
): void {
  saveCallback = saveFn
  isProcessingGetter = processingGetter
}

// ---- Helpers ----

function clockLabel(gs: GameState): string {
  return `${formatDay(gs.world.clock)}, ${formatClock(gs.world.clock)}`
}

// ---- syncRoomNPCs ----

/**
 * Update currentRoom.npcs from world.npcs at the player's location.
 * World NPCs override local NPC list for their location; room-only NPCs
 * (not tracked in world.npcs) are preserved.
 */
export function syncRoomNPCs(): void {
  if (!simState) return
  const gs = simState
  const playerLoc = locationKey(gs.player.location)
  const worldNpcNames = new Set(
    gs.world.npcs
      .filter(n => n.location === playerLoc && !isPlayerEchoNpc(gs, n.name))
      .map(n => n.name)
  )
  const localOnly = gs.currentRoom.npcs.filter(n => !worldNpcNames.has(n.name) && !isPlayerEchoNpc(gs, n.name))
  const worldAtLoc = gs.world.npcs
    .filter(n => n.location === playerLoc && !isPlayerEchoNpc(gs, n.name))
    .map(wn => ({
      name: wn.name,
      identity: wn.identity,
      lore: wn.lore,
      sex: normalizeSex(wn.sex),
      intentions: wn.intentions,
      feelings: wn.feelings,
      relationship: wn.relationship,
      memory: wn.memory,
      inventory: wn.inventory ?? [],
      activeEffects: wn.activeEffects ?? [],
      dormantEffects: wn.dormantEffects ?? [],
    }))
  gs.currentRoom.npcs = [...worldAtLoc, ...localOnly]
}

// ---- seedWorldNPCs ----

/**
 * On new game start: promote currentRoom.npcs into world.npcs.
 * Each gets a stable id and a starting location = player's position.
 */
export function seedWorldNPCs(): void {
  if (!simState) return
  const gs = simState
  if (gs.world.npcs.length > 0) return; // already seeded
  const loc = locationKey(gs.player.location)
  gs.world.npcs = gs.currentRoom.npcs
    .filter(n => !isPlayerEchoNpc(gs, n.name))
    .map(n => ({
    id: generateNpcId(n.name),
    name: n.name,
    identity: n.identity,
    lore: n.lore,
    sex: normalizeSex(n.sex),
    intentions: n.intentions ?? [],
    feelings: n.feelings ?? {},
    location: loc,
    lastActed: 0,
    relationship: n.relationship ?? 0,
    memory: n.memory ?? [],
    inventory: n.inventory ?? [],
    activeEffects: n.activeEffects ?? [],
    dormantEffects: n.dormantEffects ?? [],
  }))
}

// ---- applyWorldTickUpdate ----

function prettifyFlagLabel(key: string, value: string | number | boolean | null | undefined): string {
  const base = key.replace(/_/g, ' ').toUpperCase()
  if (value === true) return base
  if (value === false || value == null) return base
  return `${base}: ${value}`
}

function sameStringList(a: string[] = [], b: string[] = []): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function shouldAppendDormantEffect(
  existing: Array<{ label: string; source: string }>,
  incoming: { label: string; source: string },
): boolean {
  const recent = existing.slice(-8)
  return !recent.some(e => e.label === incoming.label && e.source === incoming.source)
}

function applyWorldTickUpdate(
  gs: GameState,
  update: Awaited<ReturnType<typeof aiWorldTick>>,
): { events: WorldEvent[]; roomChanged: boolean } {
  const newEvents: WorldEvent[] = []
  const timeLabel = clockLabel(gs)
  const playerLoc = locationKey(gs.player.location)
  const inCombat = !!gs.combat
  let npcsMoved = false
  let roomFlagsChangedAtPlayerLoc = false
  const clockResults = applyProgressClockUpdates(gs, update.clockUpdates)
  for (const hit of clockResults.advanced) {
    const status = formatProgressClock(hit.clock)
    const text = hit.after >= hit.clock.segments && hit.before < hit.clock.segments
      ? `Clock filled (${hit.scope}): ${status}${hit.clock.trigger ? ` — ${hit.clock.trigger}` : ''}`
      : `Clock advanced (${hit.scope}): ${status}`
    newEvents.push({ time: timeLabel, text, type: 'world' })
  }
  // NPC moves
  for (const nu of update.npcUpdates ?? []) {
    const npc = gs.world.npcs.find(n => n.id === nu.id)
    if (!npc) continue
    // Guard: don't move NPCs at player's location while in combat
    if (inCombat && npc.location === playerLoc) continue
    if (nu.moveTo) { npc.location = nu.moveTo; npcsMoved = true; }
    if (nu.intentions) npc.intentions = nu.intentions
    if (nu.feelings) npc.feelings = { ...npc.feelings, ...nu.feelings }
    if (nu.relationship !== undefined) npc.relationship = nu.relationship
    if (nu.activeEffects) {
      const nextEffects = [...new Set(nu.activeEffects.map(e => String(e).trim()).filter(Boolean))].slice(0, 6)
      if (!sameStringList(npc.activeEffects ?? [], nextEffects)) npc.activeEffects = nextEffects
    }
    if (nu.dormantEffectsAdd) {
      npc.dormantEffects = npc.dormantEffects ?? []
      const incoming = {
        label: nu.dormantEffectsAdd.label,
        source: nu.dormantEffectsAdd.source || 'world_tick',
      }
      if (shouldAppendDormantEffect(npc.dormantEffects, incoming)) {
        npc.dormantEffects.push({
          label: incoming.label,
          source: incoming.source,
          attr: nu.dormantEffectsAdd.attr,
          expiredAt: totalMinFromClock(gs.world.clock),
        })
        if (npc.dormantEffects.length > 60) npc.dormantEffects = npc.dormantEffects.slice(-60)
      }
    }
    if (nu.memoryAdd) {
      npc.memory = npc.memory ?? []
      npc.memory.push(nu.memoryAdd)
      if (npc.memory.length > 10) npc.memory.shift(); // Keep only last 10 memories
    }
    dispatcher.trigger('world-sim', TOPICS.world.npcMoved, { id: npc.id, location: npc.location })
  }

  // Spontaneous NPC spawns
  for (const sn of update.newNpcs ?? []) {
    if (!gs.atlas.nodes[sn.location]) continue
    const id = generateNpcId(sn.name)
    gs.world.npcs.push({
      id,
      name: sn.name,
      identity: sn.identity,
      lore: sn.lore,
      sex: normalizeSex(sn.sex),
      location: sn.location,
      lastActed: totalMinFromClock(gs.world.clock),
      intentions: sn.intentions,
      feelings: {},
      relationship: 0,
      memory: [],
      inventory: [],
      activeEffects: [],
      dormantEffects: [],
    })
    newEvents.push({ time: timeLabel, text: `A new presence is felt in the world: ${sn.name}.`, type: 'npc' })
  }

  // Spontaneous item spawns
  for (const si of update.newItems ?? []) {
    const node = gs.atlas.nodes[si.location]
    if (!node) continue
    node.items = node.items ?? []
    if (node.items.length < 24) {
      node.items.push(si.name)
      newEvents.push({ time: timeLabel, text: `An object has appeared: ${si.name}.`, type: 'world' })
    }
  }

  // Quest updates (shared transition rules from types.ts)
  for (const qu of update.questUpdates ?? []) {
    // Guardrail: world simulation may activate/fail/reawaken quests, but completion
    // must come from explicit player action flow.
    if (!canWorldTickSetQuestStatus(qu.newStatus)) continue
    const quest = gs.journal.find(q => q.id === qu.id)
    if (!quest) continue
    const allowed = QUEST_VALID_TRANSITIONS[quest.status]
    if (!allowed?.includes(qu.newStatus)) continue
    quest.status = qu.newStatus
    if (qu.newStatus === 'completed' || qu.newStatus === 'failed') {
      quest.completed = totalMinFromClock(gs.world.clock)
    }
    const note = qu.note ?? `Quest "${quest.title}" is now ${qu.newStatus}.`
    newEvents.push({ time: timeLabel, text: note, type: 'quest' })
    dispatcher.trigger('world-sim', TOPICS.world.questUpdated, { id: quest.id, newStatus: qu.newStatus })
  }

  // New atlas nodes (max 2 per tick, must be adjacent to existing nodes)
  let newNodeCount = 0
  for (const nn of (update.newNodes ?? []).slice(0, 2)) {
    if (gs.atlas.nodes[nn.key]) continue
    const coords = parseLocKey(nn.key)
    if (!coords) continue
    const [nx, ny, nz] = coords
    const adjacent = Object.keys(gs.atlas.nodes).some(k => {
      const c = parseLocKey(k)
      if (!c) return false
      const [ex, ey, ez] = c
      return (Math.abs(nx - ex) === 1 && ny === ey && nz === ez) || 
             (nx === ex && Math.abs(ny - ey) === 1 && nz === ez) ||
             (nx === ex && ny === ey && Math.abs(nz - ez) === 1)
    })
    if (!adjacent || newNodeCount >= 2) continue
    gs.atlas.nodes[nn.key] = { name: nn.name, desc: nn.desc }
    const adjKey = Object.keys(gs.atlas.nodes).find(k => {
      if (k === nn.key) return false
      const c = parseLocKey(k)
      if (!c) return false
      const [ex, ey, ez] = c
      return (Math.abs(nx - ex) === 1 && ny === ey && nz === ez) || 
             (nx === ex && Math.abs(ny - ey) === 1 && nz === ez) ||
             (nx === ex && ny === ey && Math.abs(nz - ez) === 1)
    })
    if (adjKey) {
      const c = parseLocKey(adjKey)!
      gs.atlas.edges.push({ x1: c[0], y1: c[1], z1: c[2], x2: nx, y2: ny, z2: nz })
    }

    newNodeCount++
    newEvents.push({ time: timeLabel, text: `A new location emerges: ${nn.name}.`, type: 'world' })
  }

  // Room flag updates (written to atlas node flags, NOT currentRoom)
  for (const rfu of update.roomFlagUpdates ?? []) {
    const node = gs.atlas.nodes[rfu.roomKey]
    if (!node) continue
    node.flags = node.flags ?? {}
    node.dormantFlags = node.dormantFlags ?? []
    for (const [k, v] of Object.entries(rfu.flags)) {
      if (v === null) {
        const prev = node.flags[k]
        delete node.flags[k]
        if (prev !== undefined) {
          node.dormantFlags.push({
            key: k,
            label: prettifyFlagLabel(k, prev),
            clearedAt: totalMinFromClock(gs.world.clock),
          })
          if (node.dormantFlags.length > 60) node.dormantFlags = node.dormantFlags.slice(-60)
        }
      } else {
        node.flags[k] = v
      }
    }
    if (rfu.roomKey === playerLoc) {
      gs.currentRoom.flags = { ...node.flags }
      gs.currentRoom.dormantFlags = [...(node.dormantFlags ?? [])]
      roomFlagsChangedAtPlayerLoc = true
    }
  }

  for (const text of update.worldEvents ?? []) {
    newEvents.push({ time: timeLabel, text, type: 'world' })
  }

  // Append to worldLog in-place, FIFO cap
  gs.worldLog.push(...newEvents)
  if (gs.worldLog.length > MAX_WORLD_LOG) {
    gs.worldLog = gs.worldLog.slice(-MAX_WORLD_LOG)
  }

  return { events: newEvents, roomChanged: npcsMoved || roomFlagsChangedAtPlayerLoc }
}

export function applyWorldTickUpdateForTest(
  gs: GameState,
  update: Awaited<ReturnType<typeof aiWorldTick>>,
): { events: WorldEvent[]; roomChanged: boolean } {
  return applyWorldTickUpdate(gs, update)
}

// ---- runWorldTick ----

export async function runWorldTick(triggeredSpawns: string[] = []): Promise<void> {
  if (!simState || isSimulating) return
  isSimulating = true
  try {
    const gs = simState
    console.log('[WorldSim] Running world tick...')
    const update = await aiWorldTick(gs, triggeredSpawns)
    const { events, roomChanged } = applyWorldTickUpdate(gs, update)
    gs.world.lastTick = totalMinFromClock(gs.world.clock)
    // Only sync room NPCs if any actually moved
    if (roomChanged) {
      syncRoomNPCs()
      dispatcher.trigger('world-sim', TOPICS.game.roomChanged, gs.currentRoom)
    }

    dispatcher.trigger('world-sim', TOPICS.world.ticked, events)
    saveCallback?.().catch(console.warn)
    console.log(`[WorldSim] Tick complete — ${events.length} events`)
  } catch (e) {
    console.error('[WorldSim] Tick failed:', e)
  } finally {
    isSimulating = false
  }
}

// ---- runWorldEnrichment ----

export async function runWorldEnrichment(): Promise<void> {
  if (!simState || isSimulating) return
  isSimulating = true
  try {
    const gs = simState
    console.log('[WorldSim] Running world enrichment...')
    const enrichment = await aiWorldEnrich(gs)
    if (enrichment.narrative) gs.world.narrative = enrichment.narrative
    gs.world.lastEnrichment = totalMinFromClock(gs.world.clock)
    if (enrichment.newQuests?.length) {
      const totalMin = totalMinFromClock(gs.world.clock)
      for (const q of enrichment.newQuests) {
        if (!gs.journal.find(j => j.id === q.id)) {
          gs.journal.push({ ...q, created: totalMin })
        }
      }
    }

    for (const ne of enrichment.npcEnrichments ?? []) {
      const npc = gs.world.npcs.find(n => n.id === ne.id)
      if (!npc) continue
      if (ne.lore) npc.lore = ne.lore
      if (ne.intentions) npc.intentions = ne.intentions
      if (ne.relationship !== undefined) npc.relationship = ne.relationship
      if (ne.memoryAdd) {
        npc.memory = npc.memory ?? []
        npc.memory.push(ne.memoryAdd)
        if (npc.memory.length > 10) npc.memory.shift()
      }
    }

    dispatcher.trigger('world-sim', TOPICS.world.enriched, { narrative: enrichment.narrative })
    saveCallback?.().catch(console.warn)
    console.log('[WorldSim] Enrichment complete.')
  } catch (e) {
    console.error('[WorldSim] Enrichment failed:', e)
  } finally {
    isSimulating = false
  }
}

import { findEdge } from './atlas-graph'

// ... existing code ...

// ---- processTravelRisk ----

/**
 * Calculates consequences of traveling across an edge.
 * Drains vitals and potentially triggers events based on risk.
 */
export function processTravelRisk(gs: GameState, fromX: number, fromY: number, fromZ: number, toX: number, toY: number, toZ: number): {
  vitalityLoss: number
  spiritLoss: number
  eventTriggered?: string
} {
  const edge = findEdge(gs.atlas, fromX, fromY, fromZ, toX, toY, toZ)
  const risk = edge?.risk ?? 0.05 // Default low risk if undefined
  
  const timeLabel = clockLabel(gs)
  
  // Base drain + risk-scaled drain
  const vitalityLoss = Math.round(2 + (risk * 15))
  const spiritLoss = Math.round(1 + (risk * 10))
  
  gs.player.vitals.vitality = Math.max(1, gs.player.vitals.vitality - vitalityLoss)
  gs.player.vitals.spirit = Math.max(0, gs.player.vitals.spirit - spiritLoss)
  
  let eventTriggered: string | undefined
  
  // Check for random encounter based on risk
  if (Math.random() < (risk * 0.8)) {
    eventTriggered = risk > 0.5 ? 'A dangerous encounter during travel!' : 'You encountered a minor obstacle while traveling.'
    gs.worldLog.push({
      time: timeLabel,
      text: eventTriggered,
      type: 'environment'
    })
  }
  
  return { vitalityLoss, spiritLoss, eventTriggered }
}

// ---- checkAndTick ----

export async function checkAndTick(): Promise<boolean> {
  if (!simState || isSimulating || isProcessingGetter()) return false
  const gs = simState
  const now = totalMinFromClock(gs.world.clock)
  const last = gs.world.lastTick ?? 0
  if (now - last < GAME_MIN_PER_TICK) return false

  const triggeredSpawns: string[] = []
  const playerLoc = locationKey(gs.player.location)
  const node = gs.atlas.nodes[playerLoc]
  
  if (node?.spawns?.length) {
    const lastCheck = node.lastSpawnCheck ?? now
    const elapsedMinutes = now - lastCheck
    const elapsedHours = elapsedMinutes / 60
    
    if (elapsedHours > 0) {
      for (const rule of node.spawns) {
        const pTrigger = 1 - Math.pow(1 - rule.chancePerHour, elapsedHours)
        if (Math.random() < pTrigger) {
          triggeredSpawns.push(`Level ${rule.level > 0 ? '+' : ''}${rule.level} ${rule.archetype}, friendliness: ${rule.friendliness}, alertness: ${rule.alertness}, loot info: ${rule.lootInfo}`)
        }
      }
      node.lastSpawnCheck = now
    }
  }

  await runWorldTick(triggeredSpawns)
  return true
}

// ---- scheduleEnrichmentIfNeeded ----

export function scheduleEnrichmentIfNeeded(): void {
  if (!simState) return
  const gs = simState
  if (!gs.world.narrative || gs.journal.length < 2) {
    if (enrichmentTimer !== null) clearTimeout(enrichmentTimer)
    enrichmentTimer = setTimeout(() => {
      enrichmentTimer = null
      if (simState && !isSimulating) runWorldEnrichment().catch(console.warn)
    }, 500)
  }
}

// ---- startWorldTick / stopWorldTick ----

export function startWorldTick(): void {
  stopWorldTick()
  tickTimer = setInterval(() => {
    if (!isSimulating && !isProcessingGetter() && simState) {
      checkAndTick().catch(console.warn)
    }
  }, TICK_INTERVAL_MS)
  console.log('[WorldSim] World tick started (interval: 90s real-time)')
}

export function stopWorldTick(): void {
  if (tickTimer !== null) {
    clearInterval(tickTimer)
    tickTimer = null
    console.log('[WorldSim] World tick stopped')
  }
}
